import { makeStyles } from '@material-ui/core';
import React from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '@material-ui/core/Modal';

const useStyles = makeStyles(() => ({
  modalStyle: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  box: {
    backgroundColor: '#fff',
    width: 'min(760px, 96vw)',
    maxHeight: '94vh',
    borderRadius: '4px',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  head: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0.4rem 0.6rem',
    borderBottom: '1px solid #dedede',
  },
  title: {
    fontSize: '13px',
    fontWeight: 'bold',
    color: '#0E2336',
    margin: '0 !important',
  },
  close: {
    backgroundColor: '#fff',
    color: '#86898C',
    fontWeight: 'bold',
    fontSize: '12px',
    lineHeight: '12px',
    border: '1px solid #86898C',
    borderRadius: '4px',
    cursor: 'pointer',
    padding: '0.2rem 0.5rem',
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
  },
  body: {
    flex: 1,
    minHeight: '60vh',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  frame: {
    width: '100%',
    height: '84vh',
    border: 'none',
  },
  message: {
    fontSize: '13px',
    color: '#323030',
    textAlign: 'center',
    padding: '1rem 0.75rem',
    margin: 0,
  },
}));

/**
 * The vendor's own trade-detail page for one game bet, shown inside the app.
 *
 * The page is the vendor's, not ours: it is embedded rather than rebuilt because a bet's legs, odds and combo
 * structure only exist on their side (our callback bodies carry identifiers and money, nothing else), and a
 * rebuild would have to keep pace with their settlement rules forever. Embedding also keeps one shape for both
 * engines - a sports ticket renders its own detail inline, a games ticket brings its reel report in behind it.
 *
 * The URL is a ticket: it is handed straight to the frame, and the frame is opened only while the modal is up,
 * so nothing is left behind for the next screen or the next player. The vendor's page is the only way through by
 * choice - it is not offered again in a tab of its own or a second window.
 *
 * @param open       - whether the modal is up
 * @param handleClose - closes the modal (the frame goes with it)
 * @param url        - the vendor's page, or null while it is still being asked for / when there is none
 * @param loading    - true while the vendor is being asked
 */
const TradeDetailModal = ({ open, handleClose, url, loading }) => {
  const classes = useStyles();
  const { t } = useTranslation();

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="trade-detail-title"
      className={classes.modalStyle}
    >
      <div className={classes.box}>
        <div className={classes.head}>
          <p className={classes.title} id="trade-detail-title">
            <span className={`lang-${localStorage.getItem('language')}`}>
              {t('Trade Detail')}
            </span>
          </p>
          <div className={classes.actions}>
            {/* the ticket is shown here and nowhere else: the vendor's page is not offered in a tab of its own */}
            <button className={classes.close} onClick={handleClose}>
              <span className={`lang-${localStorage.getItem('language')}`}>
                {t('Close')}
              </span>
            </button>
          </div>
        </div>
        <div className={classes.body}>
          {loading ? (
            <p className={classes.message}>
              <span className={`lang-${localStorage.getItem('language')}`}>
                {t('Loading detail...')}
              </span>
            </p>
          ) : url ? (
            <iframe
              title="trade-detail"
              src={url}
              referrerPolicy="no-referrer"
              className={classes.frame}
            />
          ) : (
            <p className={classes.message}>
              <span className={`lang-${localStorage.getItem('language')}`}>
                {t('Unable to load detail. Please try again.')}
              </span>
            </p>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default TradeDetailModal;
